/**
 * Date utilities for displaying game dates and times.
 *
 * IMPORTANT: Game dates from the API are "wall clock" times without timezone info.
 * Example: "2026-06-25T10:24:00" means 10:24 AM on June 25, 2026.
 * We use local date methods (NOT UTC methods) to display dates exactly as received.
 */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Format a date string to { day, month }
 * Example: "2026-06-25T10:24:00" => { day: "25", month: "Jun" }
 */
export function formatGameDate(dateStr: string): {
  day: string;
  month: string;
} {
  const d = new Date(dateStr);
  return {
    day: String(d.getDate()),
    month: MONTHS[d.getMonth()],
  };
}

/**
 * Format a date string to time (e.g., "10:24 AM")
 * Example: "2026-06-25T10:24:00" => "10:24 AM"
 */
export function formatGameTime(dateStr: string): string {
  const d = new Date(dateStr);
  const hours = d.getHours();
  const minutes = d.getMinutes();

  // Convert to 12-hour format
  const hour12 = hours % 12 || 12;
  const ampm = hours >= 12 ? "PM" : "AM";
  const minuteStr = minutes.toString().padStart(2, "0");

  return `${hour12}:${minuteStr} ${ampm}`;
}

/**
 * Format a date string to full date (e.g., "Thursday, June 25, 2026")
 * Example: "2026-06-25T10:24:00" => "Thursday, June 25, 2026"
 */
export function formatGameDateLong(dateStr: string): string {
  const d = new Date(dateStr);
  const weekday = WEEKDAYS_LONG[d.getDay()];
  const day = d.getDate();
  const year = d.getFullYear();

  const monthFull = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ][d.getMonth()];

  return `${weekday}, ${monthFull} ${day}, ${year}`;
}

/**
 * Format a date string to short date (e.g., "Jun 25")
 * Example: "2026-06-25T10:24:00" => "Jun 25"
 */
export function formatGameDateShort(dateStr: string): string {
  const d = new Date(dateStr);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * Format a date string to date with weekday (e.g., "Thu, Jun 25")
 * Example: "2026-06-25T10:24:00" => "Thu, Jun 25"
 */
export function formatGameDateWithWeekday(dateStr: string): string {
  const d = new Date(dateStr);
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/**
 * Format a date string to date and time (e.g., "Jun 25, 2026 10:24 AM")
 * Example: "2026-06-25T10:24:00" => "Jun 25, 2026 10:24 AM"
 */
export function formatGameDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const month = MONTHS[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();

  // Convert to 12-hour format
  const hour12 = hours % 12 || 12;
  const ampm = hours >= 12 ? "PM" : "AM";
  const minuteStr = minutes.toString().padStart(2, "0");

  return `${month} ${day}, ${year} ${hour12}:${minuteStr} ${ampm}`;
}

/**
 * Format a date string to a compact date and time.
 * Example: "2026-06-25T10:24:00" => "Thu, Jun 25, 2026 · 10:24 AM"
 *
 * Short enough to hold one line beside a status badge on a phone, while still
 * carrying the year so an archived season's games stay unambiguous.
 */
export function formatGameDateTimeShort(dateStr: string): string {
  const d = new Date(dateStr);
  const weekday = WEEKDAYS[d.getDay()];
  const month = MONTHS[d.getMonth()];

  return `${weekday}, ${month} ${d.getDate()}, ${d.getFullYear()} · ${formatGameTime(
    dateStr,
  )}`;
}

/**
 * Format a date string for datetime-local input (YYYY-MM-DDTHH:mm)
 * Example: "2026-06-25T10:24:00" => "2026-06-25T10:24"
 */
export function formatGameDateForInput(dateStr: string): string {
  return formatDateForInput(new Date(dateStr));
}

/**
 * Format a Date for a datetime-local input (YYYY-MM-DDTHH:mm).
 * Uses local date methods so the wall clock time is preserved as-is.
 */
export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Format a date string to its full weekday name (e.g., "Thursday")
 * Example: "2026-06-25T10:24:00" => "Thursday"
 */
export function formatWeekdayLong(dateStr: string): string {
  return WEEKDAYS_LONG[new Date(dateStr).getDay()];
}

/**
 * Format a date string to long format with time (e.g., "Thursday, June 25, 2026, 10:24 AM")
 * Example: "2026-06-25T10:24:00" => "Thursday, June 25, 2026, 10:24 AM"
 */
export function formatGameDateTimeLong(dateStr: string): string {
  const d = new Date(dateStr);
  const weekday = WEEKDAYS_LONG[d.getDay()];
  const day = d.getDate();
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();

  const monthFull = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ][d.getMonth()];

  // Convert to 12-hour format
  const hour12 = hours % 12 || 12;
  const ampm = hours >= 12 ? "PM" : "AM";
  const minuteStr = minutes.toString().padStart(2, "0");

  return `${weekday}, ${monthFull} ${day}, ${year}, ${hour12}:${minuteStr} ${ampm}`;
}
